import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaUniversity, FaUsers, FaTrophy, FaStar, FaLaptopCode, FaAward, FaMedal, FaGraduationCap, FaCertificate, FaGlobeAmericas } from 'react-icons/fa';

const EducationSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(180deg, rgba(0, 212, 255, 0.02) 0%, rgba(255, 255, 255, 0.02) 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  }
`;

const MainTitle = styled(motion.h2)`
  font-size: 2.8rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  background: linear-gradient(135deg, #ffffff 0%, #00d4ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const EducationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
`;

const EducationCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2.5rem;
  transition: all 0.3s ease;
  max-width: 800px;
  width: 100%;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(0, 212, 255, 0.3);
    transform: translateY(-5px);
  }
`;

const SectionDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  margin: 2rem 0;
`;

const SectionTitle = styled.h3`
  color: #00d4ff;
  font-size: 1.3rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ActivitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ActivityItem = styled.div`
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 12px;
  padding: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 212, 255, 0.08);
    border-color: rgba(0, 212, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const EducationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const EducationIcon = styled.div`
  font-size: 2rem;
  color: #00d4ff;
`;

const EducationInfo = styled.div``;

const SchoolName = styled.h3`
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 0.3rem;
`;

const Degree = styled.p`
  color: #00d4ff;
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const Period = styled.p`
  color: #999999;
  font-size: 0.9rem;
`;



const GPAScore = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const GPANumber = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
`;

const GPAMax = styled.span`
  color: #999999;
  font-size: 1rem;
`;

const GPAProgress = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const GPAFill = styled.div<{ percentage: number }>`
  height: 100%;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  width: ${props => props.percentage}%;
  transition: width 1s ease;
`;

const GPAGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GPAItem = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 1rem;
`;

const GPAItemTitle = styled.h4`
  color: #00d4ff;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const CreditsInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const CreditsTitle = styled.h4`
  color: #ffffff;
  font-size: 1rem;
  margin-bottom: 0.8rem;
  text-align: center;
`;

const CreditsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CreditItem = styled.div`
  text-align: center;
`;

const CreditLabel = styled.div`
  color: #cccccc;
  font-size: 0.8rem;
  margin-bottom: 0.3rem;
`;

const CreditValue = styled.div`
  color: #00d4ff;
  font-size: 1.2rem;
  font-weight: 600;
`;

const CreditProgress = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const CreditFill = styled.div<{ percentage: number }>`
  height: 100%;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  width: ${props => props.percentage}%;
  transition: width 1s ease;
`;

const Courses = styled.div`
  margin-top: 1rem;
`;

const CoursesTitle = styled.h4`
  color: #ffffff;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const CourseList = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CourseItem = styled.li`
  background: rgba(255, 255, 255, 0.1);
  color: #cccccc;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
`;



const ActivityIcon = styled.div`
  color: #00d4ff;
  font-size: 1.2rem;
  margin-bottom: 0.8rem;
`;

const ActivityTitle = styled.h5`
  color: #ffffff;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const ActivityDescription = styled.p`
  color: #cccccc;
  font-size: 0.85rem;
  line-height: 1.5;
  margin-bottom: 0.5rem;
`;

const ActivityPeriod = styled.span`
  color: #999999;
  font-size: 0.75rem;
  font-weight: 500;
`;




const HonorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HonorCard = styled(motion.div)<{ type: string }>`
  background: ${props => {
    if (props.type === '인증') return 'linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 193, 7, 0.1))';
    if (props.type === '학술') return 'linear-gradient(135deg, rgba(156, 39, 176, 0.15), rgba(103, 58, 183, 0.1))';
    if (props.type === '공모전') return 'linear-gradient(135deg, rgba(0, 212, 255, 0.15), rgba(0, 153, 204, 0.1))';
    if (props.type === '성적') return 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 193, 7, 0.1))';
    if (props.type === '장학금') return 'linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(56, 142, 60, 0.1))';
    return 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))';
  }};
  border: 1px solid ${props => {
    if (props.type === '인증') return 'rgba(255, 152, 0, 0.3)';
    if (props.type === '학술') return 'rgba(156, 39, 176, 0.3)';
    if (props.type === '공모전') return 'rgba(0, 212, 255, 0.3)';
    if (props.type === '성적') return 'rgba(255, 215, 0, 0.3)';
    if (props.type === '장학금') return 'rgba(76, 175, 80, 0.3)';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  border-radius: 15px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => {
      if (props.type === '인증') return 'linear-gradient(90deg, #ff9800, #ffc107)';
      if (props.type === '학술') return 'linear-gradient(90deg, #ab47bc, #7e57c2)';
      if (props.type === '공모전') return 'linear-gradient(90deg, #00d4ff, #0099cc)';
      if (props.type === '성적') return 'linear-gradient(90deg, #ffd700, #ffc107)';
      if (props.type === '장학금') return 'linear-gradient(90deg, #4caf50, #388e3c)';
      return 'linear-gradient(90deg, #ffffff, #cccccc)';
    }};
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    border-color: ${props => {
      if (props.type === '인증') return 'rgba(255, 152, 0, 0.5)';
      if (props.type === '학술') return 'rgba(156, 39, 176, 0.5)';
      if (props.type === '공모전') return 'rgba(0, 212, 255, 0.5)';
      if (props.type === '성적') return 'rgba(255, 215, 0, 0.5)';
      if (props.type === '장학금') return 'rgba(76, 175, 80, 0.5)';
      return 'rgba(255, 255, 255, 0.4)';
    }};
  }
`;

const HonorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const HonorIcon = styled.div<{ type: string }>`
  font-size: 2rem;
  color: ${props => {
    if (props.type === '인증') return '#ffb74d';
    if (props.type === '학술') return '#ce93d8';
    if (props.type === '공모전') return '#00d4ff';
    if (props.type === '성적') return '#ffd700';
    if (props.type === '장학금') return '#4caf50';
    return '#ffffff';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => {
    if (props.type === '인증') return 'rgba(255, 152, 0, 0.2)';
    if (props.type === '학술') return 'rgba(156, 39, 176, 0.2)';
    if (props.type === '공모전') return 'rgba(0, 212, 255, 0.2)';
    if (props.type === '성적') return 'rgba(255, 215, 0, 0.2)';
    if (props.type === '장학금') return 'rgba(76, 175, 80, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
`;

const HonorContent = styled.div`
  flex: 1;
`;

const HonorTitle = styled.h4`
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
  line-height: 1.4;
`;

const HonorType = styled.span<{ type: string }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  background: ${props => {
    if (props.type === '인증') return 'rgba(255, 152, 0, 0.2)';
    if (props.type === '학술') return 'rgba(156, 39, 176, 0.2)';
    if (props.type === '공모전') return 'rgba(0, 212, 255, 0.2)';
    if (props.type === '성적') return 'rgba(255, 215, 0, 0.2)';
    if (props.type === '장학금') return 'rgba(76, 175, 80, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  color: ${props => {
    if (props.type === '인증') return '#ffb74d';
    if (props.type === '학술') return '#ce93d8';
    if (props.type === '공모전') return '#00d4ff';
    if (props.type === '성적') return '#ffd700';
    if (props.type === '장학금') return '#4caf50';
    return '#ffffff';
  }};
  border: 1px solid ${props => {
    if (props.type === '인증') return 'rgba(255, 152, 0, 0.3)';
    if (props.type === '학술') return 'rgba(156, 39, 176, 0.3)';
    if (props.type === '공모전') return 'rgba(0, 212, 255, 0.3)';
    if (props.type === '성적') return 'rgba(255, 215, 0, 0.3)';
    if (props.type === '장학금') return 'rgba(76, 175, 80, 0.3)';
    return 'rgba(255, 255, 255, 0.2)';
  }};
`;

const HonorDate = styled.p`
  color: #999999;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const Education: React.FC = () => {
  const educationData = {
    school: '계명대학교',
    degree: '컴퓨터공학과',
    period: '2021년 3월 - ',
    gpa: 4.24,
    maxGpa: 4.5,
    majorGpa: 4.36,
    majorMaxGpa: 4.5,
    totalCredits: 130,
    majorCredits: 69,
    completedCredits: 133,
    completedMajorCredits: 73,
    courses: [
      '자료구조', '알고리즘', '웹 프로그래밍', '데이터베이스',
      '운영체제', '네트워크', '소프트웨어공학', '인공지능', '캡스톤디자인'
    ],
    icon: <FaUniversity />
  };

  const activitiesData = [
    {
      title: 'CES 2026 참여 (미국 라스베가스)',
      description: '다른 기관과 협업하여 미국 라스베가스에서 개최된 CES 2026에 직접 참여했습니다. 세계 최대 규모의 전자제품 박람회에서 글로벌 기술 트렌드를 직접 체험하고, 국제적인 네트워킹을 통해 전문성을 확장하는 소중한 경험을 했습니다.',
      period: '2026년 1월',
      icon: <FaGlobeAmericas />,
      category: '대외활동'
    },
    {
      title: '프랑스 EPITECH 해외 연수 (국제 해커톤)',
      description: '프랑스 EPITECH에서 진행하는 해외 연수에 참여하여, 타국 및 타 교 학생들과 팀을 이루어 국제 해커톤을 진행했습니다. 제한된 시간 안에 아이디어를 구현하고 발표하며 글로벌 협업과 프로젝트 완성도를 높이는 경험을 했습니다.',
      period: '2026년 6월 - 2026년 7월',
      icon: <FaGlobeAmericas />,
      category: '대외활동'
    },
    {
      title: '멋쟁이사자처럼 대학 14기 계명대 부회장',
      description: '멋쟁이사자처럼 대학 14기 계명대학교 운영진으로 부회장을 맡아 동아리 운영, 행정 업무, 회원 관리 및 행사 기획을 담당했습니다. 또한 풀스택 세션을 진행하며 동아리원들을 대상으로 React와 Java Spring Boot 기반의 풀스택 강의를 진행했습니다.',
      period: '2026년 2월 - 2027년 2월',
      icon: <FaUsers />,
      category: '동아리 활동'
    },
    {
      title: '멋쟁이사자처럼 대학 PBL 검증 참여',
      description: '멋쟁이사자처럼 대학에서 진행한 PBL 프로젝트의 결과물을 기능 및 사용성 관점에서 검증하고, 개선 포인트를 정리해 팀에 피드백을 제공했습니다.',
      period: '2026년 2월',
      icon: <FaLaptopCode />,
      category: '대외활동'
    },
    {
      title: '멋쟁이사자처럼 대학 13기 해커톤 (팀장)',
      description: '멋쟁이사자처럼 대학 13기에서 진행된 해커톤에 참여하여 팀 프로젝트를 진행했습니다. 제한된 시간 내에 창의적인 아이디어를 구현하고 협업을 통해 완성도 높은 결과물을 만들어내는 경험을 했습니다.',
      period: '2025년 8월',
      icon: <FaLaptopCode />,
      category: '해커톤'
    },
    {
      title: 'GenAI 해커톤 교내 대회',
      description: '계명대학교에서 개최된 GenAI 해커톤 교내 대회에 참여했습니다. 생성형 AI 기술을 활용한 혁신적인 프로젝트를 개발하고, AI/머신러닝 분야의 실무 경험을 쌓을 수 있었습니다.',
      period: '2024년 11월',
      icon: <FaLaptopCode />,
      category: '해커톤'
    },
    {
      title: '동아리 총무부장',
      description: '동아리 운영 및 행정 업무 총괄',
      period: '2023년 9월 - 2024년 6월',
      icon: <FaUsers />,
      category: '동아리 활동'
    },
    {
      title: 'IT 동아리 활동',
      description: 'IT 관련 프로젝트 및 스터디 활동',
      period: '2025년 3월 - 2025년 12월',
      icon: <FaLaptopCode />,
      category: '동아리 활동'
    },
    {
      title: '코디브맵 서포터즈 코디버디 1기 수료',
      description: '코디브맵 서포터즈 프로그램에 참여하여 개발 커뮤니티 활동 및 기술 공유 활동을 진행했습니다. 다양한 개발자들과의 네트워킹과 지식 공유를 통해 전문성을 향상시켰습니다.',
      period: '2025년',
      icon: <FaGlobeAmericas />,
      category: '대외활동'
    }
  ];

  const honorsData = [
    {
      title: 'K-STAR S단계 최종 인증 (F영역)',
      subtitle: 'FACE 역량과 성과를 종합 평가해 선발하는 교내 최고단계 인재 인증 · 2026-1 전교 6명 선정',
      date: '2026년 1학기',
      type: '인증',
      icon: <FaStar />
    },
    {
      title: '한국정보기술학회 우수논문상(동상)',
      subtitle: '2026 하계종합학술대회 대학생논문경진대회',
      date: '2026년 1학기',
      type: '학술',
      icon: <FaTrophy />
    },
    {
      title: '지역혁신 인재양성 연합 페스티발 공모전 우수상',
      subtitle: '데이터 분석 부문',
      date: '2025년 2학기',
      type: '공모전',
      icon: <FaTrophy />
    },
    {
      title: 'AIDEA 공모전 수상',
      subtitle: 'AI+IDEA',
      date: '2025년 1학기',
      type: '공모전',
      icon: <FaAward />
    },
    {
      title: '캡스톤디자인 우수상',
      subtitle: '프로젝트 우수성 인정',
      date: '2025년 2학기',
      type: '공모전',
      icon: <FaTrophy />
    },
    {
      title: '캡스톤디자인 장려상',
      subtitle: '프로젝트 기획·구현 성과 인정',
      date: '2026년 1학기',
      type: '공모전',
      icon: <FaTrophy />
    },
    {
      title: '학과 1등',
      subtitle: '성적 우수',
      date: '2024년 1학기',
      type: '성적',
      icon: <FaMedal />
    },
    {
      title: '학과 1등',
      subtitle: '성적 우수',
      date: '2025년 2학기',
      type: '성적',
      icon: <FaMedal />
    },
    {
      title: '학과 1등',
      subtitle: '성적 우수',
      date: '2026년 1학기',
      type: '성적',
      icon: <FaMedal />
    },
    {
      title: '성적장학금',
      subtitle: '우수 성적',
      date: '2022년 1학기',
      type: '장학금',
      icon: <FaGraduationCap />
    },
    {
      title: '성적장학금',
      subtitle: '우수 성적',
      date: '2024년 2학기',
      type: '장학금',
      icon: <FaGraduationCap />
    },
    {
      title: '성적장학금',
      subtitle: '우수 성적',
      date: '2025년 2학기',
      type: '장학금',
      icon: <FaGraduationCap />
    },
    {
      title: '성적장학금',
      subtitle: '우수 성적',
      date: '2026년 1학기',
      type: '장학금',
      icon: <FaGraduationCap />
    },
    {
      title: '외부장학금 수상',
      subtitle: '손태희장학재단',
      date: '2024년 2학기',
      type: '장학금',
      icon: <FaCertificate />
    },
    {
      title: '푸른등대기부장학금 수상',
      subtitle: '외부 장학금',
      date: '2024년 2학기',
      type: '장학금',
      icon: <FaCertificate />
    },
    {
      title: '푸른등대기부장학금 수상',
      subtitle: '두나무UDC IT·블록체인 진로 특화, 학기당 300만원(1학기, 11명 선발)',
      date: '2026년 1학기',
      type: '장학금',
      icon: <FaCertificate />
    }
  ];

  const calculatePercentage = (gpa: number | null, maxGpa: number | null) => {
    if (!gpa || !maxGpa) return 0;
    return (gpa / maxGpa) * 100;
  };

  return (
    <EducationSection id="education">
      <MainTitle
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        Education
      </MainTitle>
      
      <EducationContainer>
        <EducationCard
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <EducationHeader>
            <EducationIcon>{educationData.icon}</EducationIcon>
            <EducationInfo>
              <SchoolName>{educationData.school}</SchoolName>
              <Degree>{educationData.degree}</Degree>
              <Period>{educationData.period}</Period>
            </EducationInfo>
          </EducationHeader>
          
          <CreditsInfo>
            <CreditsTitle>이수 학점 현황</CreditsTitle>
            <CreditsGrid>
              <CreditItem>
                <CreditLabel>전체 이수 학점</CreditLabel>
                <CreditValue>{educationData.completedCredits} / {educationData.totalCredits}</CreditValue>
                <CreditProgress>
                  <CreditFill 
                    percentage={(educationData.completedCredits / educationData.totalCredits) * 100}
                  />
                </CreditProgress>
              </CreditItem>
              <CreditItem>
                <CreditLabel>전공 이수 학점</CreditLabel>
                <CreditValue>{educationData.completedMajorCredits} / {educationData.majorCredits}</CreditValue>
                <CreditProgress>
                  <CreditFill 
                    percentage={(educationData.completedMajorCredits / educationData.majorCredits) * 100}
                  />
                </CreditProgress>
              </CreditItem>
            </CreditsGrid>
          </CreditsInfo>

          <GPAGrid>
            <GPAItem>
              <GPAItemTitle>전체 평점 (GPA)</GPAItemTitle>
              <GPAScore>
                <GPANumber>{educationData.gpa}</GPANumber>
                <GPAMax>/ {educationData.maxGpa}</GPAMax>
              </GPAScore>
              <GPAProgress>
                <GPAFill 
                  percentage={calculatePercentage(educationData.gpa, educationData.maxGpa)}
                />
              </GPAProgress>
            </GPAItem>
            <GPAItem>
              <GPAItemTitle>전공 평점 (Major GPA)</GPAItemTitle>
              <GPAScore>
                <GPANumber>{educationData.majorGpa}</GPANumber>
                <GPAMax>/ {educationData.majorMaxGpa}</GPAMax>
              </GPAScore>
              <GPAProgress>
                <GPAFill 
                  percentage={calculatePercentage(educationData.majorGpa, educationData.majorMaxGpa)}
                />
              </GPAProgress>
            </GPAItem>
          </GPAGrid>
          
          <Courses>
            <CoursesTitle>주요 수강 과목</CoursesTitle>
            <CourseList>
              {educationData.courses.map((course, courseIndex) => (
                <CourseItem key={courseIndex}>{course}</CourseItem>
              ))}
            </CourseList>
          </Courses>

          <SectionDivider />

          <SectionTitle>
            <FaUsers /> 주요 활동
          </SectionTitle>
          
          <ActivitiesGrid>
            {activitiesData.map((activity, index) => (
              <ActivityItem key={index}>
                <ActivityIcon>{activity.icon}</ActivityIcon>
                <ActivityTitle>{activity.title}</ActivityTitle>
                <ActivityDescription>{activity.description}</ActivityDescription>
                <ActivityPeriod>{activity.period}</ActivityPeriod>
              </ActivityItem>
            ))}
          </ActivitiesGrid>

          <SectionDivider />

          <SectionTitle>
            <FaTrophy /> 수상 및 성과
          </SectionTitle>
          
          <HonorsGrid>
            {honorsData.map((honor, honorIndex) => (
              <HonorCard
                key={honorIndex}
                type={honor.type}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: honorIndex * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
              >
                <HonorHeader>
                  <HonorIcon type={honor.type}>{honor.icon}</HonorIcon>
                  <HonorContent>
                    <HonorType type={honor.type}>{honor.type}</HonorType>
                    <HonorTitle>{honor.title}</HonorTitle>
                    {honor.subtitle && (
                      <p style={{ color: '#cccccc', fontSize: '0.85rem', marginTop: '0.3rem' }}>
                        {honor.subtitle}
                      </p>
                    )}
                    <HonorDate>
                      <FaStar style={{ fontSize: '0.7rem', color: '#999999' }} />
                      {honor.date}
                    </HonorDate>
                  </HonorContent>
                </HonorHeader>
              </HonorCard>
            ))}
          </HonorsGrid>
        </EducationCard>
      </EducationContainer>
    </EducationSection>
  );
};

export default Education;
